/**
 * useUISpec — Client hook for calling the generate-ui-spec edge function
 *
 * Supports two modes:
 * 1. BATCH mode (default) — generateObject() returns complete spec at once
 * 2. STREAMING mode — streamObject() sends partial specs via SSE for progressive rendering
 *
 * Streaming is enabled when:
 * - enableStreaming option is true (caller checks PostHog flag dd-feat-streaming-results)
 * - Premium tier (drama_plus or dramax)
 *
 * Falls back to a static spec on error or timeout (5s).
 * Falls back to batch mode if streaming fails.
 *
 * Returns: { spec, isLoading, error, fallback, isStreaming }
 *
 * Split into:
 * - use-ui-spec-utils.ts (buildFallbackSpec, parseSSEStream)
 * - use-ui-spec-streaming.ts (streaming SSE path)
 * - use-ui-spec-batch.ts (standard batch path)
 *
 * Task: [#783.S3] [NocoDB #786] | Streaming: [#1158.P0.4] | Split: [#2675]
 * Agent: Diego <diego@liftping.net>
 */

import { useState, useEffect, useRef } from "react";
import type { Spec } from "@json-render/core";
import type { DramaAnalysisResponse } from "@dramadetector/shared";
import { fetchStreamingSpec } from "./use-ui-spec-streaming";
import { fetchBatchSpec } from "./use-ui-spec-batch";
import {
  buildFallbackSpec,
  parseSSEStream,
  EDGE_FN_TIMEOUT_MS,
  STREAMING_TIMEOUT_MS,
  STREAMING_FIRST_CHUNK_TIMEOUT_MS,
  type UISpecTier,
} from "./use-ui-spec-utils";

// Re-export UISpecTier so existing imports from "./use-ui-spec" keep working
export type { UISpecTier };

export interface UseUISpecOptions {
  /**
   * Enable streaming mode for progressive rendering.
   * Caller should check PostHog flag dd-feat-streaming-results before enabling.
   * Only effective for premium tiers (drama_plus, dramax).
   * Default: false (batch mode)
   */
  enableStreaming?: boolean;
}

export interface UseUISpecResult {
  /** The DDUISpec (null while loading or during fallback pre-construction) */
  spec: Spec | null;
  /** True while the edge function call is in-flight */
  isLoading: boolean;
  /** Error object if the call failed (timeout or HTTP error) */
  error: Error | null;
  /** True when the static fallback spec is active (on error) */
  fallback: boolean;
  /** True while streaming partial updates are arriving (streaming mode only) */
  isStreaming: boolean;
}

// Re-export constants and utilities so existing imports from "./use-ui-spec" keep working
export {
  EDGE_FN_TIMEOUT_MS,
  STREAMING_TIMEOUT_MS,
  STREAMING_FIRST_CHUNK_TIMEOUT_MS,
  buildFallbackSpec,
  parseSSEStream,
};

// ============================================================================
// useUISpec hook
// ============================================================================

/**
 * useUISpec — Fetch a DDUISpec from the generate-ui-spec edge function
 *
 * @param analysisResult  Full DramaAnalysisResponse
 * @param userTier        Server-verified user tier (from useTier())
 * @param options         Optional configuration (enableStreaming)
 *
 * @returns { spec, isLoading, error, fallback, isStreaming }
 */
export function useUISpec(
  analysisResult: DramaAnalysisResponse | null | undefined,
  userTier: UISpecTier,
  options?: UseUISpecOptions
): UseUISpecResult {
  const [spec, setSpec] = useState<Spec | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fallback, setFallback] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);

  // Track most recent analysis ID to prevent stale updates
  const lastAnalysisIdRef = useRef<string | null>(null);
  // Track active SSE reader for cleanup
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);

  // Determine if streaming should be used for this request
  const enableStreaming = options?.enableStreaming ?? false;
  const isPremiumTier = userTier === "drama_plus" || userTier === "dramax";
  const shouldStream = enableStreaming && isPremiumTier;

  useEffect(() => {
    if (!analysisResult) {
      return;
    }

    const analysisId = analysisResult.id;
    let cancelled = false;
    const abortController = new AbortController();

    // Reset state for new fetch
    lastAnalysisIdRef.current = analysisId;
    setIsLoading(true);
    setError(null);
    setFallback(false);
    setSpec(null);
    setIsStreaming(false);

    /**
     * Activate fallback: set error + fallback + static spec.
     */
    function activateFallback(err: Error) {
      if (cancelled) return;
      setError(err);
      setFallback(true);
      setSpec(buildFallbackSpec(analysisResult!, userTier));
      setIsLoading(false);
      setIsStreaming(false);
    }

    const timeoutMs = shouldStream ? STREAMING_TIMEOUT_MS : EDGE_FN_TIMEOUT_MS;
    const timeoutId = setTimeout(() => {
      if (!cancelled) {
        cancelled = true;
        activateFallback(
          new Error(`generate-ui-spec timed out after ${timeoutMs / 1000}s`)
        );
      }
    }, timeoutMs);

    const isCancelled = () => cancelled;
    const clearMainTimeout = () => clearTimeout(timeoutId);

    // Shared spec-success callback
    const onSpec = (s: Spec) => {
      setSpec(s);
      setError(null);
      setFallback(false);
    };

    // Batch fetch params (used directly and as streaming fallback)
    const batchParams = {
      analysisId,
      analysisResult,
      userTier,
      abortController,
      isCancelled,
      onSpec,
      onLoadingEnd: () => setIsLoading(false),
      clearMainTimeout,
    };

    // Wrapper for batch that handles its own timeout on streaming fallback
    async function batchWithTimeout() {
      clearMainTimeout();
      const batchTimeoutId = setTimeout(() => {
        if (!cancelled) {
          cancelled = true;
          activateFallback(
            new Error(`generate-ui-spec batch fallback timed out after ${EDGE_FN_TIMEOUT_MS / 1000}s`)
          );
        }
      }, EDGE_FN_TIMEOUT_MS);

      try {
        await fetchBatchSpec(batchParams);
        clearTimeout(batchTimeoutId);
      } catch (batchErr) {
        clearTimeout(batchTimeoutId);
        if (cancelled) return;
        cancelled = true;
        const e = batchErr instanceof Error ? batchErr : new Error(String(batchErr));
        activateFallback(e);
      }
    }

    // Choose streaming or batch path
    const fetchFn = shouldStream
      ? () => fetchStreamingSpec({
          analysisId,
          analysisResult,
          userTier,
          abortController,
          isCancelled,
          onSpec,
          onPartialSpec: (s: Spec) => setSpec(s),
          onDone: (s: Spec) => {
            onSpec(s);
            setIsLoading(false);
            setIsStreaming(false);
          },
          onError: (err: Error) => activateFallback(err),
          onStreamingStart: () => setIsStreaming(true),
          onStreamingEnd: () => setIsStreaming(false),
          onLoadingEnd: () => setIsLoading(false),
          setReader: (r) => { readerRef.current = r; },
          clearMainTimeout,
          fetchBatchSpec: batchWithTimeout,
        })
      : () => fetchBatchSpec(batchParams);

    fetchFn().catch((err) => {
      if (cancelled) return;
      clearTimeout(timeoutId);
      cancelled = true;
      const e = err instanceof Error ? err : new Error(String(err));
      activateFallback(e);
    });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      abortController.abort();
      if (readerRef.current) {
        readerRef.current.cancel().catch(() => {});
        readerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisResult?.id, userTier, shouldStream]);

  return { spec, isLoading, error, fallback, isStreaming };
}
